import React, { useEffect, useMemo, useState } from "react";
import Joyride, { STATUS } from "react-joyride";
const TOUR_STORAGE_KEY = "ff_builder_tour_completed_v1";
function hasCompletedTour() {
    if (typeof window === "undefined")
        return false;
    try {
        return window.localStorage.getItem(TOUR_STORAGE_KEY) === "true";
    }
    catch {
        return false;
    }
}
function markTourCompleted() {
    if (typeof window === "undefined")
        return;
    try {
        window.localStorage.setItem(TOUR_STORAGE_KEY, "true");
    }
    catch {
        // ignore storage errors
    }
}
export const BuilderTour = ({ trigger, onClose }) => {
    const [run, setRun] = useState(false);
    // Define the tour steps. We rely on data attributes so the builder layout
    // can remain flexible while keeping targets stable.
    const steps = useMemo(() => [
        {
            target: '[data-tour-id="builder-chat-panel"]',
            content: "Welcome to Fluid Form. This quick tour will show you how to go from an empty canvas to a live, shareable form. This is our AI Form builder and it is the best way to get started. Just specify your form requirements and let it take care of the rest.",
            placement: "top",
            disableBeacon: true,
        },
        {
            target: '[data-tour-id="builder-preview"]',
            content: "This is the live preview area of your form. You can experience the entire form filling journey here and the best part, the submissions here don't count towards your usage limit :)",
            placement: "right",
        },
        {
            target: '[data-tour-id="builder-canvas"]',
            content: "This is the builder area, used to make manual updates to your form. The AI form builder is fully compatible with the manual builder, so you can use both to build/edit/update your form.",
            placement: "left",
        },
        {
            target: '[data-tour-id="builder-publish-tab"]',
            content: "When you have built your form, this is where you can publish it for your users and continue developing iterations of it as versions.",
            placement: "bottom",
        },
        {
            target: '[data-tour-id="builder-publish-button"]',
            content: "This is the area to publish and manage your form versions. Any changes that you make to the form will only go live once you publish them and will be available as a version. You can control which version the users should see when they visit the form, by setting a version as active. Once you publish your first version, you will start seeing the various options via which you can share your form with the users.",
            placement: "top",
        },
        {
            target: '[data-tour-id="builder-root"]',
            content: "You are all set. The tour is complete. You can always come back to this tour by clicking the ? button in the bottom right corner.",
            placement: "center",
        },
    ], []);
    useEffect(() => {
        if (trigger === "auto" && !hasCompletedTour()) {
            setRun(true);
        }
        else if (trigger === "manual") {
            setRun(true);
        }
    }, [trigger]);
    const handleCallback = (data) => {
        const { status, action, step } = data;
        // When we reach the publish-tab step at any point, dispatch a custom event
        // so the BuilderPanelGroup can switch tabs programmatically. This avoids
        // relying on DOM clicks that might be blocked by Joyride's overlay.
        if (step?.target === '[data-tour-id="builder-publish-tab"]' && typeof window !== 'undefined') {
            window.dispatchEvent(new Event('ff-open-publish-tab'));
        }
        // End the tour immediately when the user finishes, skips or explicitly closes it.
        // Do NOT react to other actions (like prev/next) so Back/Next keep working normally.
        if (status === STATUS.FINISHED ||
            status === STATUS.SKIPPED ||
            action === "close") {
            markTourCompleted();
            setRun(false);
            onClose?.();
        }
    };
    if (!run) {
        return null;
    }
    return (React.createElement(Joyride, { steps: steps, continuous: true, showSkipButton: true, showProgress: true, disableOverlayClose: true, disableScrollParentFix: true, scrollToFirstStep: true, locale: {
            back: 'Back',
            close: 'Close',
            last: 'Ok, got it!',
            next: 'Next',
            skip: 'Skip tour',
        }, styles: {
            options: {
                zIndex: 10000,
                primaryColor: '#f97316', // FluidForm orange
                backgroundColor: '#111827',
                textColor: '#f9fafb',
                arrowColor: '#111827',
                overlayColor: 'rgba(15,23,42,0.6)',
                spotlightShadow: '0 0 0 9999px rgba(15,23,42,0.6)',
            },
            buttonNext: {
                backgroundColor: '#f97316',
                borderRadius: '4px',
                fontWeight: 500,
            },
            buttonBack: {
                color: '#e5e7eb',
            },
            buttonClose: {
                color: '#9ca3af',
            },
            tooltip: {
                borderRadius: 12,
                boxShadow: '0 18px 45px rgba(15,23,42,0.55)',
                padding: '16px 18px 14px',
                maxWidth: 380,
            },
            tooltipTitle: {
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 4,
            },
            tooltipContent: {
                fontSize: 13,
                lineHeight: 1.5,
            },
        }, callback: handleCallback, run: run }));
};
